import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";

import * as api from "@/lib/api";
import type { ApiWorkspace, WorkspaceDomain } from "@/lib/api";

/**
 * Live workspace data backed by the FastAPI backend.
 *
 * We keep the legacy field names that the Lovable UI expects (`risk`,
 * `riskScore`, `entities`, `signals`, `updatedAt`, `ontology`) by deriving
 * them client-side from the API response. As real risk/signal/entity counts
 * land, these mocks shrink.
 */

export type Risk = "high" | "medium" | "low";
export type { WorkspaceDomain };

export interface Workspace {
  id: string;
  name: string;
  domain: WorkspaceDomain;
  description: string;
  published: boolean;
  subdomain?: string;
  risk: Risk;
  riskScore: number;
  entities: number;
  signals: number;
  updatedAt: string;
  ontology: string;
  ontology_id: string | null;
  // Phase 6 — surfaces share state for badge rendering without an extra
  // request. Optional so older API responses still adapt cleanly.
  org_shared?: boolean;
  share_origin?: "owner" | "org_admin" | "explicit_member" | "org_shared" | null;
}

function adapt(ws: ApiWorkspace): Workspace {
  return {
    id: ws.id,
    name: ws.name,
    domain: ws.domain,
    description: ws.description ?? "",
    published: false,
    risk: "low",
    riskScore: 0,
    entities: ws.stats?.entities ?? 0,
    signals: ws.stats?.signals ?? 0,
    updatedAt: relative(ws.updated_at),
    ontology: ws.ontology_id ? "Custom" : "—",
    ontology_id: ws.ontology_id,
    org_shared: ws.org_shared ?? false,
    share_origin: ws.share_origin ?? null,
  };
}

function relative(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

interface Ctx {
  workspaces: Workspace[];
  isLoading: boolean;
  getById: (id: string) => Workspace | undefined;
  createWorkspace: (input: {
    name: string;
    description: string;
    domain: WorkspaceDomain;
    ontology_id?: string | null;
  }) => Promise<Workspace>;
  renameWorkspace: (id: string, name: string) => Promise<void>;
  updateWorkspace: (
    id: string,
    patch: { name?: string; description?: string; domain?: WorkspaceDomain },
  ) => Promise<void>;
  deleteWorkspace: (id: string) => Promise<void>;
  togglePublish: (id: string, subdomain?: string) => void;
}

const WorkspaceCtx = createContext<Ctx | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["workspaces"],
    queryFn: api.workspaces.list,
    // If unauthenticated, the API throws 401 — keep empty list and let the
    // route guards send the user to /login.
    retry: (_, err) => {
      if (err instanceof api.ApiError && err.status === 401) return false;
      return true;
    },
  });

  const createMut = useMutation({
    mutationFn: (input: {
      name: string;
      description: string;
      domain: WorkspaceDomain;
      ontology_id?: string | null;
    }) =>
      api.workspaces.create({
        name: input.name,
        description: input.description,
        domain: input.domain,
        ontology_id: input.ontology_id ?? null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspaces"] }),
  });

  const updateMut = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { name?: string; description?: string; domain?: WorkspaceDomain };
    }) => api.workspaces.update(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspaces"] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.workspaces.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspaces"] }),
  });

  const workspaces = (data ?? []).map(adapt);

  const value: Ctx = {
    workspaces,
    isLoading,
    getById: (id) => workspaces.find((w) => w.id === id),
    createWorkspace: async (input) => {
      const ws = await createMut.mutateAsync({
        name: input.name,
        description: input.description,
        domain: input.domain,
        ontology_id: input.ontology_id ?? null,
      });
      return adapt(ws);
    },
    renameWorkspace: async (id, name) => {
      await updateMut.mutateAsync({ id, patch: { name } });
    },
    updateWorkspace: async (id, patch) => {
      await updateMut.mutateAsync({ id, patch });
    },
    deleteWorkspace: async (id) => {
      await deleteMut.mutateAsync(id);
    },
    togglePublish: () => {
      // Publishing API lands in milestone 4+. Surface stays for the Lovable UI.
    },
  };

  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}

export function useWorkspaces() {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) throw new Error("useWorkspaces must be used within WorkspaceProvider");
  return ctx;
}
